import { Image } from 'expo-image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { isDesktop } from '@/core';
import { BLACK } from '@/style/colors';
import { Button } from '@/ui';
import { Icon } from '@/ui/icon';

import { CheckItem } from '../check-item';
import { Cancel } from '../icons/cancel';
import { ChevronDown } from '../icons/chevron-down';
import { Input } from '../input';
import { Text } from '../text';
import { Tooltip } from '../tooltip';

export interface DropdownOption {
  id: string;
  value: string;
  title: string;
  caption?: string;
  imageUrl?: string;
}

interface DropdownProps {
  mode?: 'single' | 'multi';
  appearance?: 'default' | 'filter';
  label?: string;
  options: DropdownOption[];
  placeHolderText: string;
  onSelect: (selected: DropdownOption[]) => void;
  onClear?: () => void;
  onClose?: () => void;
  tooltipText?: string;
  values?: string[];
  style?: any;
  optionsContainerStyle?: any;
  error?: string;
  defaultExpanded?: boolean;
  disabled?: boolean;
  filterSearchable?: boolean;
  /** When set, option list is not filtered locally (e.g. server-side search). */
  onFilterTextChange?: (text: string) => void;
}

export default function Dropdown({
  mode = 'single',
  appearance = 'default',
  label,
  options,
  placeHolderText,
  onSelect,
  onClear,
  onClose,
  tooltipText,
  values = [],
  style,
  optionsContainerStyle,
  error,
  defaultExpanded = false,
  disabled = false,
  filterSearchable,
  onFilterTextChange,
}: DropdownProps) {
  const { theme, styles: dropdownStyles } = useStyles(dropdownStylesheet);
  const isFilter = appearance === 'filter';

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selectedText, setSelectedText] = useState('');
  const [filterText, setFilterText] = useState('');
  const [tempSelectedOptions, setTempSelectedOptions] = useState<string[]>(
    () => values || []
  );

  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    () => values || []
  );

  const dropdownRef = useRef<any>(null);
  const filteredOptions = useMemo(() => {
    if (!options) {
      return [];
    }

    if (onFilterTextChange) {
      return options;
    }

    return options.filter((option) =>
      option.title.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [onFilterTextChange, options, filterText]);

  const updateFilterText = useCallback(
    (text: string) => {
      setFilterText(text);
      onFilterTextChange?.(text);
    },
    [onFilterTextChange]
  );

  const closeDropdown = useCallback(() => {
    setExpanded(false);
    setFilterText('');
    onFilterTextChange?.('');
    onClose?.();
  }, [onClose, onFilterTextChange]);

  const showFilterSearch = filterSearchable ?? (options?.length ?? 0) > 8;

  const renderFilterOptions = () =>
    filteredOptions.map((option, index) => {
      const optionKey = option.id ?? option.value ?? `${option.title}-${index}`;
      const isSelected = selectedOptions.includes(option.value);

      return (
        <Pressable
          key={`option-${optionKey}`}
          style={[
            dropdownStyles.filterOption,
            isSelected ? dropdownStyles.filterOptionSelected : undefined,
          ]}
          onPress={() => {
            if (mode === 'single') {
              onSelect([option]);
            }

            setSelectedText(option.title);
            setTempSelectedOptions([option.value]);
            updateFilterText('');
            closeDropdown();
          }}
        >
          <View style={dropdownStyles.filterOptionRow}>
            {option.imageUrl ? (
              <Image
                source={{ uri: option.imageUrl }}
                style={dropdownStyles.filterOptionImage}
                contentFit="contain"
              />
            ) : null}
            <View style={dropdownStyles.filterOptionTextBlock}>
              <Text type="bodyRegular" style={dropdownStyles.filterOptionText}>
                {option.title}
              </Text>
              {option.caption ? (
                <Text
                  type="caption1"
                  style={dropdownStyles.filterOptionCaption}
                >
                  {option.caption}
                </Text>
              ) : null}
            </View>
          </View>
        </Pressable>
      );
    });

  const renderFilterTrigger = () => (
    <Pressable
      style={dropdownStyles.filterTrigger}
      onPress={() => setExpanded((current) => !current)}
    >
      <Text
        type="bodyRegular"
        style={dropdownStyles.filterTriggerText}
        numberOfLines={1}
      >
        {selectedText || placeHolderText}
      </Text>
      <Icon
        type="naked"
        icon={<ChevronDown />}
        fillColor={theme.colors.icon.color}
        width={12}
        height={12}
      />
    </Pressable>
  );

  const renderOptionsList = () => (
    <>
      {filteredOptions.map((option, index) => {
        const optionKey =
          option.id ?? option.value ?? `${option.title}-${index}`;

        if (mode === 'multi') {
          return (
            <CheckItem
              key={`option-${optionKey}`}
              text={option.title}
              caption={option.caption}
              active={tempSelectedOptions.includes(option.value)}
              onPress={() =>
                setTempSelectedOptions((prev) =>
                  prev.includes(option.value)
                    ? prev.filter((v) => v !== option.value)
                    : [...prev, option.value]
                )
              }
              type="checkbox"
            />
          );
        }

        return (
          <Pressable
            key={`option-${optionKey}`}
            hitSlop={8}
            onPress={() => {
              if (mode === 'single') {
                onSelect([option]);
              }

              setSelectedText(option.title);
              setTempSelectedOptions([option.value]);
              updateFilterText('');
              closeDropdown();
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <Text type="bodyRegular" style={{ flexShrink: 1 }}>
                {option.title}
              </Text>
              {option.caption ? (
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {option.caption}
                </Text>
              ) : null}
            </View>
          </Pressable>
        );
      })}
      {filteredOptions.length === 0 ? (
        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          No results found
        </Text>
      ) : null}
    </>
  );

  // useEffect(() => {
  //   if (mode === 'multi' && selectedOptions.length > 0) {
  //     setSelectedText(`${selectedOptions.length} Selected`);
  //   }
  // }, [mode, selectedOptions]);

  // useEffect(() => {
  //   if (mode === 'single' && selectedOptions.length > 0) {
  //     options.find((option) => {
  //       if (option.value === selectedOptions[0]) {
  //         setSelectedText(option.title);
  //       }
  //     });
  //   }
  // }, [mode, selectedOptions, options]);

  useEffect(() => {
    setSelectedOptions(values);
    setTempSelectedOptions(values);

    if (mode === 'multi' && values.length > 0) {
      setSelectedText(`${values.length} Selected`);
    }
    if (mode === 'single') {
      const found =
        values.length > 0
          ? options?.find((option) => option.value === values[0])
          : undefined;
      setSelectedText(found?.title ?? '');
    }
  }, [mode, options, JSON.stringify(values)]);

  useEffect(() => {
    if (disabled) setExpanded(false);
  }, [disabled]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isDesktop()) {
          closeDropdown();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  if (isDesktop() && isFilter) {
    return (
      <View
        ref={dropdownRef}
        style={[
          dropdownStyles.filterRoot,
          expanded ? dropdownStyles.filterRootExpanded : undefined,
          style,
        ]}
      >
        {renderFilterTrigger()}
        {expanded ? (
          <View style={[dropdownStyles.filterMenu, optionsContainerStyle]}>
            {showFilterSearch ? (
              <View style={dropdownStyles.filterSearch}>
                <Input
                  autoFocus
                  label=""
                  value={filterText}
                  onChangeText={updateFilterText}
                  placeholder="Search"
                  containerStyle={dropdownStyles.filterSearchInput}
                />
              </View>
            ) : null}
            <ScrollView
              style={dropdownStyles.filterOptionsScroll}
              keyboardShouldPersistTaps="always"
              nestedScrollEnabled
            >
              <View style={dropdownStyles.filterOptions}>
                {renderFilterOptions()}
              </View>
              {filteredOptions.length === 0 ? (
                <Text type="bodyRegular" style={dropdownStyles.filterEmpty}>
                  No results found
                </Text>
              ) : null}
            </ScrollView>
          </View>
        ) : null}
        {error ? (
          <Text style={{ color: theme.colors.input.error.helperText }}>
            {error}
          </Text>
        ) : null}
      </View>
    );
  }

  if (isDesktop()) {
    return (
      <View ref={dropdownRef} style={[{ position: 'relative' }, style]}>
        {expanded ? (
          <View
            style={{
              gap: 8,
            }}
          >
            {label || tooltipText ? (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {label ? (
                  <Text style={{ paddingLeft: 4, paddingRight: 4 }}>
                    {label}
                  </Text>
                ) : null}

                {tooltipText ? <Tooltip text={tooltipText} /> : null}
              </View>
            ) : null}
            <Input
              autoFocus
              label={''}
              value={filterText}
              onChangeText={updateFilterText}
              onFocus={() => setExpanded(true)}
              placeholder={'Search'}
              containerStyle={{ width: '100%' }}
            />

            <ScrollView
              style={{
                maxHeight: 200,
                marginTop: 8,
                marginBottom: 16,
                borderColor: theme.colors.card.default.borderColor,
                borderWidth: 1,
                borderRadius: 8,
                ...optionsContainerStyle,
              }}
              contentContainerStyle={{
                padding: 16,
                gap: 8,
              }}
              keyboardShouldPersistTaps="always"
            >
              {renderOptionsList()}
            </ScrollView>
          </View>
        ) : (
          <View style={{ gap: 8 }}>
            <Pressable
              disabled={disabled}
              onPress={() => {
                if (!disabled) setExpanded(true);
              }}
            >
              <Input
                editable={false}
                pointerEvents="none"
                disabled={disabled}
                label={label || ''}
                value={selectedText}
                placeholder={placeHolderText}
                rightIcon={
                  disabled ? undefined : selectedOptions.length > 0 ? (
                    <Icon
                      type="naked"
                      icon={<Cancel />}
                      fillColor={theme.colors.icon.color}
                      width={12}
                      height={12}
                    />
                  ) : (
                    <Icon
                      type="naked"
                      icon={<ChevronDown />}
                      fillColor={theme.colors.icon.color}
                      width={12}
                      height={12}
                    />
                  )
                }
                onRightIconPress={() => {
                  if (disabled) return;

                  if (selectedOptions.length > 0) {
                    setSelectedOptions([]);
                    setSelectedText('');
                    onClear?.();
                  } else {
                    setExpanded(true);
                  }
                }}
                tooltipText={tooltipText}
              />
            </Pressable>

            {error ? (
              <Text style={{ color: theme.colors.input.error.helperText }}>
                {error}
              </Text>
            ) : null}
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={[{ position: 'relative', gap: 8 }, style]}>
      {isFilter ? (
        renderFilterTrigger()
      ) : (
        <Pressable
          disabled={disabled}
          onPress={() => {
            if (!disabled) setExpanded(true);
          }}
        >
          <Input
            editable={false}
            pointerEvents="none"
            disabled={disabled}
            label={label || ''}
            value={selectedText}
            placeholder={placeHolderText}
            rightIcon={
              disabled ? undefined : selectedOptions.length > 0 ? (
                <Icon
                  type="naked"
                  icon={<Cancel />}
                  fillColor={theme.colors.icon.color}
                  width={12}
                  height={12}
                />
              ) : (
                <Icon
                  type="naked"
                  icon={<ChevronDown />}
                  fillColor={theme.colors.icon.color}
                  width={12}
                  height={12}
                />
              )
            }
            onRightIconPress={() => {
              if (disabled) return;

              if (selectedOptions.length > 0) {
                setSelectedOptions([]);
                setSelectedText('');
                onClear?.();
              } else {
                setExpanded(true);
              }
            }}
            tooltipText={tooltipText || ''}
            inputStyle={{ width: '100%', flexShrink: 1 }}
          />
        </Pressable>
      )}

      {error ? (
        <Text style={{ color: theme.colors.input.error.helperText }}>
          {error}
        </Text>
      ) : null}

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={300}
        hasBackdrop
        backdropColor={BLACK}
        backdropOpacity={0.5}
        isVisible={expanded}
        avoidKeyboard
        style={{
          margin: 0,
          minWidth: 350,
        }}
        onBackdropPress={() => {
          closeDropdown();
        }}
        onDismiss={() => {}}
      >
        <View
          style={{
            marginTop: 'auto',
            backgroundColor: isFilter
              ? theme.colors.card.default.backgroundColor
              : theme.colors.background_2,
            borderTopRightRadius: 16,
            borderTopLeftRadius: 16,
            flexDirection: 'column',
            gap: 16,
            padding: 16,
            paddingBottom: 32,
            borderTopStartRadius: 24,
          }}
        >
          {isFilter ? (
            <>
              {showFilterSearch ? (
                <Input
                  label=""
                  value={filterText}
                  onChangeText={updateFilterText}
                  placeholder="Search"
                />
              ) : null}
              <ScrollView
                style={{ maxHeight: 320 }}
                contentContainerStyle={dropdownStyles.filterOptions}
                keyboardShouldPersistTaps="always"
              >
                {renderFilterOptions()}
              </ScrollView>
            </>
          ) : (
            <>
              <Input
                label=""
                value={filterText}
                onChangeText={updateFilterText}
                onFocus={() => setExpanded(true)}
                placeholder="Search"
              />

              <ScrollView
                style={{ maxHeight: 200, marginTop: 8, marginBottom: 16 }}
                contentContainerStyle={{ padding: 16, gap: 8 }}
                keyboardShouldPersistTaps="always"
              >
                {renderOptionsList()}
              </ScrollView>
            </>
          )}

          {mode === 'multi' ? (
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <Button
                text="Clear All"
                type="ghost"
                onPress={() => {
                  setTempSelectedOptions([]);
                  updateFilterText('');
                }}
                disabled={tempSelectedOptions.length === 0}
                style={{ flex: 1 }}
              />

              <Button
                text="Save"
                type="primary"
                onPress={() => {
                  onSelect(
                    options.filter((option) =>
                      tempSelectedOptions.includes(option.value)
                    )
                  );
                  setSelectedOptions(tempSelectedOptions);
                  setExpanded(false);
                }}
                style={{ flex: 1 }}
              />
            </View>
          ) : null}
        </View>
      </Modal>
    </View>
  );
}

const dropdownStylesheet = createStyleSheet((theme) => ({
  filterRoot: {
    position: 'relative',
    zIndex: 1,
  },
  filterRootExpanded: {
    zIndex: 20,
  },
  filterTrigger: {
    minHeight: 38,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: theme.colors.card.default.backgroundColor,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
  },
  filterTriggerText: {
    flex: 1,
    color: theme.colors.text.primary,
  },
  filterMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    backgroundColor: theme.colors.card.default.backgroundColor,
    overflow: 'hidden',
    maxHeight: 320,
  },
  filterSearch: {
    paddingHorizontal: 8,
    paddingTop: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  filterSearchInput: {
    width: '100%',
  },
  filterOptionsScroll: {
    maxHeight: 260,
  },
  filterOptions: {
    padding: 8,
    gap: 4,
  },
  filterOption: {
    paddingHorizontal: 8,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  filterOptionSelected: {
    backgroundColor: theme.colors.card.active.backgroundColor,
    borderColor: theme.colors.card.active.borderColor,
  },
  filterOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  filterOptionImage: {
    width: 48,
    height: 32,
    flexShrink: 0,
  },
  filterOptionTextBlock: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  filterOptionText: {
    color: theme.colors.text.primary,
  },
  filterOptionCaption: {
    color: theme.colors.text.secondary,
  },
  filterEmpty: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    color: theme.colors.text.secondary,
  },
}));
